import * as Sentry from '@sentry/remix';
import type {
  BasicNode,
  InlineNode,
  TiptapState,
  TPostBoostElement,
  TPostConditionSetElement,
  TPostContentElement,
  TPostDynamicContentElement,
  TPostPollElement,
  TPostRecommendationElement,
  TPostSectionElement,
  TPostSubscriberBreak,
  TPostTableCell,
  TPostTableHeader,
  TPostTableRow,
} from '@shared/dream-components';
import type { TPostParagraphElement } from '@shared/dream-components/src/postContentElement/components/nodes/paragraph/Paragraph.types';
import type { TPostTable } from '@shared/dream-components/src/postContentElement/components/nodes/table/Table.types';
import type { WritableDraft } from 'immer';
import { produce } from 'immer';

import {
  PostDynamicContentConditionOperator,
  type PostDynamicContentConditions,
} from '~/interfaces/post_dynamic_content_conditions';
import type { PostMergeTags } from '~/interfaces/post_merge_tags';

const SUBSCRIBER_ID_MERGE_TAG = '{{subscriber_id}}';
const EMAIL_MERGE_TAG = '{{email}}';
const MERGE_TAGS_WITH_HTML = ['rp_personalized_text', 'rp_upcoming_milestones'];

export type ProcessorFunctionOptions = {
  subscriberBreakElementData: TPostSubscriberBreak['attrs']['data'];
  hasSubscription: boolean;
  referralsCount: number | undefined;
  subscriptionTierIds: string[];
  shouldForcePremium: boolean;
  postMergeTags?: PostMergeTags;
  dynamicContentConditions?: PostDynamicContentConditions;
  pollsEnabled: boolean;
  enforcePaywallBreaks: boolean;
  subscriberId?: string;
  subscriberEmail?: string;
  isPremiumSubscriber: boolean;
};

export type ProcessorFunction = (
  postContentNode: WritableDraft<TPostContentElement>,
  options?: ProcessorFunctionOptions,
) => void;

export function process(
  postContentNode: TPostContentElement,
  processors: ProcessorFunction[],
  options?: ProcessorFunctionOptions,
): TPostContentElement {
  const processedNodeContent = produce(postContentNode, (draft) => {
    processors.filter(Boolean).forEach((processor) => {
      processor(draft, options);
    });
  });

  return processedNodeContent;
}

const processSubscriberGate = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  try {
    if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
      return;
    }

    // find the subscriber break node
    const subscriberBreakIndex = draft.attrs.data.content.findIndex(
      (node: BasicNode) => node.type === 'subscriberBreak',
    );

    // there is no subscriber break
    if (subscriberBreakIndex === -1) return;

    // Inject data required to render Subscriber break element
    draft.attrs.data.content[subscriberBreakIndex] = {
      ...draft.attrs.data.content[subscriberBreakIndex],
      attrs: {
        data: {
          ...(options?.subscriberBreakElementData ? { ...options.subscriberBreakElementData } : {}),
        },
      },
    };

    // remove all contents after the subscriber break
    draft.attrs.data.content = draft.attrs.data.content.slice(0, subscriberBreakIndex + 1);
  } catch (error) {
    Sentry.captureException(error);
  }
};

// Example {{foo}} {{foo bar}} {{foo|bar}} {{ foo | bar }} {{ foo bar | baz buz }}
const MERGE_TAG_PATTERN = /\{\{\s*([^|}]+?)\s*(?:\|\s*([^|}]+?)\s*)?\}\}/g;

/**
 * Recursively walks an object/array tree and replaces merge tags in string values.
 * This approach is safer than JSON.stringify/parse because it:
 * - Avoids JSON parsing errors from user content
 * - Only replaces in actual string values, not in JSON structure
 * - Handles deeply nested objects correctly
 */
const replaceMergeTagsInValue = (value: unknown, mergeTags: PostMergeTags): unknown => {
  // Handle strings - this is where merge tag replacement happens
  if (typeof value === 'string') {
    let result = value;

    // Find all merge tags in the string
    const foundMergeTags = result.match(MERGE_TAG_PATTERN);
    if (!foundMergeTags) {
      return result;
    }

    foundMergeTags.forEach((tag) => {
      // Simple merge tag (example: {{subscriber_id}})
      // biome-ignore lint/suspicious/noPrototypeBuiltins: Need to check own properties safely
      if (Object.prototype.hasOwnProperty.call(mergeTags, tag)) {
        result = result.replaceAll(tag, mergeTags[tag]);
        return;
      }

      // Merge tags with default value (example: {{first_name|Guest}})
      const isMergeTagWithDefaultValue = tag.includes('|');
      if (isMergeTagWithDefaultValue) {
        try {
          const [tagName, tagDefaultValue] = tag.replace(/^\{\{|\}\}$/g, '').split('|');
          const tagNameWithoutSpaces = tagName.replaceAll(' ', '');
          const replacementValue = mergeTags[`{{${tagNameWithoutSpaces}}}`] || tagDefaultValue.trimStart().trimEnd();
          result = result.replaceAll(tag, replacementValue);
        } catch {
          // Fail gracefully for malformed default value syntax
        }
        return;
      }

      // Tag with spaces (example: {{ subscriber_id }})
      const extractedTagName = tag.replace(/^\{\{|\}\}$/g, '').trim();
      if (mergeTags[`{{${extractedTagName}}}`]) {
        result = result.replaceAll(tag, mergeTags[`{{${extractedTagName}}}`]);
      }
    });

    return result;
  }

  // Handle arrays - recurse into each element
  if (Array.isArray(value)) {
    return value.map((item) => replaceMergeTagsInValue(item, mergeTags));
  }

  // Handle objects - recurse into each property
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = replaceMergeTagsInValue(val, mergeTags);
    }
    return result;
  }

  // Primitives (numbers, booleans, null) - return as-is
  return value;
};

const processMergeTags = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
    return;
  }

  try {
    // Default merge tags, combined with any additional merge tags provided in options
    const mergeTags: PostMergeTags = {
      [SUBSCRIBER_ID_MERGE_TAG]: options?.subscriberId || SUBSCRIBER_ID_MERGE_TAG,
      [EMAIL_MERGE_TAG]: options?.subscriberEmail || EMAIL_MERGE_TAG,
      ...(options?.postMergeTags || {}),
    };

    // Use object-walking approach instead of JSON.stringify/parse [WEB-5442]
    // This is safer because it replaces values directly in the object tree,
    // avoiding JSON parsing errors from user-provided content
    draft.attrs.data.content = produce(draft.attrs.data.content, (contentDraft: TiptapState) => {
      return replaceMergeTagsInValue(contentDraft, mergeTags) as TiptapState;
    });
  } catch (error) {
    // Capture detailed context for debugging if the new approach still fails [WEB-5442]
    Sentry.withScope((scope) => {
      scope.setContext('merge_tags_error', {
        error_message: error instanceof Error ? error.message : String(error),
        content_structure: JSON.stringify(draft.attrs.data.content, null, 2).substring(0, 1000),
        merge_tags: JSON.stringify(options?.postMergeTags || {}, null, 2).substring(0, 500),
        subscriber_id: options?.subscriberId,
        subscriber_email: options?.subscriberEmail,
      });
      Sentry.captureException(error);
    });

    // Re-throw to maintain existing error behavior
    throw error;
  }
};

const processNodeRecursively = (node: any, options?: ProcessorFunctionOptions): any => {
  if (shouldSkipNode(node, options)) {
    return null;
  }

  switch (node.type) {
    case 'dynamicContent':
    case 'conditionSet':
    case 'table':
    case 'tableRow':
    case 'tableCell':
    case 'tableHeader':
      return produce(
        node,
        (
          draftNode:
            | TPostDynamicContentElement
            | TPostConditionSetElement
            | TPostTable
            | TPostTableRow
            | TPostTableCell
            | TPostTableHeader,
        ) => {
          if (draftNode.content) {
            draftNode.content = recursivelyProcessNodeContent(draftNode.content, options);
          }

          return draftNode;
        },
      );
    case 'section': {
      const section = processSection(node, options);
      if (!section) {
        return null;
      }

      return produce(section, (draftSection) => {
        if (!Array.isArray(draftSection.content) || draftSection.content.length === 0) {
          return draftSection;
        }

        draftSection.content = recursivelyProcessNodeContent(draftSection.content, options);

        if (draftSection?.attrs?.color) {
          draftSection.content = recursivelyApplyTextColor(draftSection.content, draftSection.attrs.color);
          return draftSection;
        }

        return draftSection;
      });
    }
    case 'poll':
      return processPollNode(node, options);
    case 'recommendation':
      return processRecommendationNode(node, options);
    case 'boost':
      return processBoostNode(node, options);
    case 'paragraph':
      return processParagraphNode(node);
    default:
      return node;
  }
};

const recursivelyProcessNodeContent = (nodeContent: any[], options?: ProcessorFunctionOptions): any => {
  if (!Array.isArray(nodeContent) || nodeContent.length === 0) {
    return nodeContent;
  }

  return nodeContent.map((childNode: any) => processNodeRecursively(childNode, options)).filter(Boolean);
};

const processData = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
    return;
  }

  draft.attrs.data.content = produce(draft.attrs.data.content, (draftContent) => {
    return draftContent.map((node: any) => processNodeRecursively(node, options)).filter(Boolean);
  });
};

const shouldSkipNode = (node: any, options?: ProcessorFunctionOptions) => {
  if (!node) {
    return true;
  }

  const nodePlatform = node?.data?.platform;
  const nodeType = node?.type;

  if (nodePlatform !== undefined && nodePlatform !== 'web') {
    return true;
  }

  if (nodeType === 'dynamicContent') {
    // For now there is just one child conditionSet
    const conditionIds = node?.content?.flatMap((content: any) => content.attrs?.conditions) ?? [];
    const operator = node?.content?.[0]?.attrs?.operator ?? PostDynamicContentConditionOperator.OR;
    const evaluatedConditions = options?.dynamicContentConditions ?? {};

    if (operator === PostDynamicContentConditionOperator.AND) {
      return !conditionIds.every((id: string) => !!evaluatedConditions[id]);
    }
    if (operator === PostDynamicContentConditionOperator.OR) {
      return !conditionIds.some((id: string) => !!evaluatedConditions[id]);
    }
    return false;
  }

  if (nodeType === 'poll' && !options?.pollsEnabled) {
    return true;
  }

  // To ensure that we don't render the paywall break block if we're not enforcing paywall breaks
  if (nodeType === 'paywallBreak' && !options?.enforcePaywallBreaks) {
    return true;
  }

  return false;
};

const encodeHtmlTagsInText = (text: string) =>
  text.replace(/<\/?[a-zA-Z][^>]*>/g, (tag) => tag.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));

const processParagraphNode = (node: TPostParagraphElement) => {
  return produce(node, (draftNode) => {
    if (!draftNode.content) {
      return draftNode;
    }

    draftNode.content = produce(draftNode.content, (draftContent) => {
      return draftContent?.map((inlineNode) => {
        if ('text' in inlineNode && !!inlineNode.text) {
          const hasMergeTagWithHtml = MERGE_TAGS_WITH_HTML.some((tag) => inlineNode.text.includes(tag));
          return {
            ...inlineNode,
            text: hasMergeTagWithHtml ? encodeHtmlTagsInText(inlineNode.text) : inlineNode.text,
            renderAsHtml: hasMergeTagWithHtml,
          };
        }
        return inlineNode;
      }) as InlineNode[];
    });

    return draftNode;
  });
};

const processPollNode = (node: TPostPollElement, options?: ProcessorFunctionOptions) => {
  if (!node.attrs.data) {
    return node;
  }

  return produce(node, (draftNode) => {
    if (!draftNode?.attrs?.data?.poll_choices || !Array.isArray(draftNode?.attrs?.data?.poll_choices)) {
      return draftNode;
    }

    draftNode.attrs.data.poll_choices = produce(draftNode.attrs.data.poll_choices, (draftPollChoices) => {
      return draftPollChoices.map((choice) => {
        return {
          ...choice,
          response_url: options?.hasSubscription && options?.subscriberId ? choice.response_url : choice.login_url,
        };
      });
    });

    return draftNode;
  });
};

const processRecommendationNode = (node: TPostRecommendationElement, options?: ProcessorFunctionOptions) => {
  if (!node?.attrs?.data?.recommended_publication) {
    return node;
  }

  return produce(node, (draftNode) => {
    draftNode.attrs.data.recommended_publication = produce(
      draftNode.attrs.data.recommended_publication,
      (draftRecommendedPublication) => {
        if (draftRecommendedPublication) {
          draftRecommendedPublication.subscribe_url = options?.hasSubscription
            ? draftRecommendedPublication.subscribe_url
            : draftRecommendedPublication.subscribe_url_anonymous;
        }
        return draftRecommendedPublication;
      },
    );

    return draftNode;
  });
};

const processBoostNode = (node: TPostBoostElement, options?: ProcessorFunctionOptions) => {
  if (!node?.attrs?.data?.recommended_publication) {
    return node;
  }

  return produce(node, (draftNode) => {
    draftNode.attrs.data.recommended_publication = produce(
      draftNode.attrs.data.recommended_publication,
      (draftRecommendedPublication) => {
        if (draftRecommendedPublication) {
          draftRecommendedPublication.subscribe_url = options?.hasSubscription
            ? draftRecommendedPublication.subscribe_url
            : draftRecommendedPublication.subscribe_url_anonymous;
        }
        return draftRecommendedPublication;
      },
    );

    return draftNode;
  });
};

export const processSection = (node: TPostSectionElement, options?: ProcessorFunctionOptions) => {
  const nodeAttrs = node.attrs;

  if (!nodeAttrs) {
    return node;
  }

  // Don't render section if it is configured to be hidden on web
  if (!nodeAttrs.showOnWebsite) {
    return null;
  }

  const isShowToTieredSubscribersBlank =
    typeof nodeAttrs.showToTieredSubscribers === 'undefined' ||
    !nodeAttrs.showToTieredSubscribers ||
    (Array.isArray(nodeAttrs.showToTieredSubscribers) && nodeAttrs.showToTieredSubscribers.length === 0);

  const onlyShowToFreeOrVisitors =
    (nodeAttrs.showToFreeSubscribers || nodeAttrs.showToNonSubscribers) &&
    nodeAttrs.showToPaidSubscribers === false &&
    isShowToTieredSubscribersBlank;

  const shouldForcePremium = options?.shouldForcePremium;

  // Hide sections that would only be visible to free/visitors if shouldForcePremium is true
  if (shouldForcePremium && onlyShowToFreeOrVisitors) {
    return null;
  }

  const forcePremiumToRender = (() => {
    if (!shouldForcePremium) {
      return false;
    }

    return nodeAttrs.showToPaidSubscribers || typeof nodeAttrs.showToTieredSubscribers !== 'undefined';
  })();

  // Force premium sections to render if force_premium? is enabled
  if (forcePremiumToRender) {
    return node;
  }

  // Content written before this feature was added should default to showing
  if (!options?.hasSubscription) {
    if (nodeAttrs.showToNonSubscribers || typeof nodeAttrs.showToNonSubscribers === 'undefined') {
      return node;
    }

    return null;
  }

  const currentSubscriptionTierIds = options?.subscriptionTierIds || [];

  const hidePremium = (() => {
    if (
      nodeAttrs.showToPaidSubscribers === false &&
      options.isPremiumSubscriber &&
      (Array.isArray(nodeAttrs.showToTieredSubscribers)
        ? nodeAttrs.showToTieredSubscribers
        : nodeAttrs.showToTieredSubscribers != null
          ? [nodeAttrs.showToTieredSubscribers]
          : []
      ).filter((id) => currentSubscriptionTierIds.includes(id)).length === 0
    ) {
      return true;
    }
  })();

  if (hidePremium) {
    return null;
  }

  const hideFree = (() => {
    return nodeAttrs.showToFreeSubscribers === false && currentSubscriptionTierIds.length === 0;
  })();

  if (hideFree) {
    return null;
  }

  const subscriptionMeetsReferralCondition = (() => {
    if (options?.referralsCount === undefined || nodeAttrs.referralCountValue === undefined) {
      return false;
    }

    switch (nodeAttrs.referralCondition) {
      case 'gt':
        return options.referralsCount > nodeAttrs.referralCountValue;
      case 'lt':
        return options.referralsCount < nodeAttrs.referralCountValue;
      case 'eq':
        return options.referralsCount === nodeAttrs.referralCountValue;
      default:
        return false;
    }
  })();

  if (nodeAttrs.showWithReferralCount && !subscriptionMeetsReferralCondition) {
    return null;
  }

  return node;
};

const recursivelyApplyTextColor = (
  content: any[],
  sectionTextColor: string,
  ignoreBlockTypes = ['button', 'codeBlock', 'genericEmbed', 'blockquoteFigure'],
) => {
  return content.map((block) => {
    if (ignoreBlockTypes.includes(block.type)) {
      return block;
    }

    if (block.type === 'text') {
      if (!block.marks || (Array.isArray(block.marks) && block.marks.length === 0)) {
        block.marks = [{ type: 'textStyle', attrs: { color: sectionTextColor } }];
      } else {
        const hasLinkMark = block.marks.some((mark: any) => mark.type === 'link');
        if (hasLinkMark) {
          return block;
        }

        const textStyleMark = block.marks.find((mark: any) => mark.type === 'textStyle');
        if (textStyleMark) {
          textStyleMark.attrs.color ||= sectionTextColor;
        } else {
          block.marks.push({ type: 'textStyle', attrs: { color: sectionTextColor } });
        }
      }
    }

    if (Array.isArray(block.content)) {
      block.content = recursivelyApplyTextColor(block.content, sectionTextColor, ignoreBlockTypes);
    }

    return block;
  });
};

export const truncateAfterPaywallBreak = (draft: WritableDraft<TPostContentElement>) => {
  if (!draft.attrs.data.content || !Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
    return;
  }

  // find the paywall break node
  const paywallBreakIndex = draft.attrs.data.content.findIndex((node: BasicNode) => node.type === 'paywallBreak');

  if (paywallBreakIndex === -1) return;

  // remove all contents after the paywall break
  draft.attrs.data.content = draft.attrs.data.content.slice(0, paywallBreakIndex + 1);
};

export const removePaywallBreak = (draft: WritableDraft<TPostContentElement>) => {
  if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
    return;
  }

  // find the paywall break node
  const paywallBreakIndex = draft.attrs.data.content.findIndex((node: BasicNode) => node.type === 'paywallBreak');

  if (paywallBreakIndex === -1) return;

  // remove the paywall break node, keep the rest of the content
  draft.attrs.data.content = [
    ...draft.attrs.data.content.slice(0, paywallBreakIndex),
    ...draft.attrs.data.content.slice(paywallBreakIndex + 1),
  ];
};

export const processors = {
  processSubscriberGate,
  truncateAfterPaywallBreak,
  removePaywallBreak,
  processMergeTags,
  processData,
  processParagraphNode,
};
